import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ApiService } from '../utils/api';
import { formatDateTime } from '../utils/datetime';
import { useToast } from './Toast';
import { useAuth } from '../contexts/AuthContext';
import DynamicBadgeRenderer from './DynamicBadgeRenderer';
import UserDetailsEditor from './UserDetailsEditor';
import LockerAssignModal from './LockerAssignModal';
import { canSharePdf } from '../utils/printWindow';
import { 
  X, 
  Printer, 
  RefreshCw,
  AlertTriangle,
  Edit3,
  RadioTower,
  Lock,
  Share2,
} from 'lucide-react';
import { eventRef } from '../utils/eventRef';

/**
 * Modal for printing badges using dynamic badge configuration
 * Can be used with QR scan results or registration data
 */
const DynamicBadgePrintModal = ({
  isOpen,
  onClose,
  badgeConfig = null,
  userData = null,
  eventId = null,
  scanResult = null,
  registrationData = null,
  title = "Print Badge",
  formSchema = [],
  onUserDataUpdate = null,
  scannedUser = null,
  rfIdEnabled = false,
  onAssignRfid = null,
  lockerEnabled = false,
  onPrintStarted = null,
  shareEnabled = false,
}) => {
  const { showSuccess, showError } = useToast();
  const { isEditor } = useAuth();
  // Viewers (and unauthenticated desk scans) only preview and print the badge.
  const badgeOnly = !isEditor();
  const badgeRendererRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);
  const [showLockerModal, setShowLockerModal] = useState(false);
  // shareEnabled is the event's per-platform opt-in; the browser must also be able to share files.
  const shareSupported = useMemo(() => shareEnabled && canSharePdf(), [shareEnabled]);
  // Built ahead of the tap: the share sheet must open within the click's short user-activation window.
  const pdfFileRef = useRef(null);
  // Initialize current user data
  useEffect(() => {
    if (scannedUser) {
      setCurrentUserData(scannedUser);
    } else if (userData) {
      setCurrentUserData({ formData: userData });
    }
  }, [scannedUser, userData]);

  // Fetch dynamic badge configuration only if not provided as prop
  const { data: fetchedBadgeConfig, isLoading: configLoading, error: configError } = useQuery({
    queryKey: ['badgeDesignerConfig', eventId],
    queryFn: () => ApiService.getBadgeDesignerConfig(eventId),
    enabled: isOpen && !!eventId && !badgeConfig, // Only fetch if not provided as prop
    retry: false
  });

  // Use provided badgeConfig or fallback to fetched one
  const finalBadgeConfig = badgeConfig || fetchedBadgeConfig;

  // Determine the user data to use for badge rendering
  const getUserData = () => {
    // Use current user data if available (for live updates during editing)
    if (currentUserData?.formData) {
      return {
        ...currentUserData.formData,
        userId: currentUserData.userId,
        qrCodeBase64: currentUserData.qrCodeBase64,
        purpose: currentUserData.purpose,
        redeemedAt: formatDateTime(currentUserData.redeemedAt, ''),
        redeemedBy: currentUserData.redeemedBy
      };
    }

    // Use provided userData first
    if (userData) {
      return userData;
    }

    if (scanResult) {
      return {
        ...scanResult.formData,
        qrCodeBase64: scanResult.qrCodeBase64,
        purpose: scanResult.purpose,
        redeemedAt: formatDateTime(scanResult.redeemedAt, ''),
        redeemedBy: scanResult.redeemedBy
      };
    }
    
    if (registrationData) {
      return registrationData;
    }
    
    return null;
  };

  const finalUserData = getUserData();

  // Handle user data changes during editing
  const handleUserDataChange = (updatedFormData) => {
    if (currentUserData) {
      setCurrentUserData({
        ...currentUserData,
        formData: updatedFormData
      });
    }
  };

  // Handle save user data
  const handleSaveUserData = async (updatedFormData) => {
    if (!currentUserData?.userId || !onUserDataUpdate) {
      showError('Cannot update user data');
      return;
    }

    setIsUpdatingUser(true);
    try {
      await onUserDataUpdate(currentUserData.userId, updatedFormData);
      
      // Refetch the complete registration data from backend to get latest data
      if (eventId && currentUserData.userId) {
        try {
          const refreshedData = await ApiService.getRegistration(eventId, currentUserData.userId);
          // Update with fresh data from backend
          setCurrentUserData({
            ...currentUserData,
            formData: refreshedData.formData || refreshedData,
            ...refreshedData
          });
        } catch (refreshError) {
          console.error('Failed to refresh user data:', refreshError);
          // If refresh fails, still update with the saved data
          setCurrentUserData({
            ...currentUserData,
            formData: updatedFormData
          });
        }
      } else {
        // Fallback to local update if we can't refresh
        setCurrentUserData({
          ...currentUserData,
          formData: updatedFormData
        });
      }
      
      setIsEditingUser(false);
      showSuccess('User details updated successfully!');
    } catch (error) {
      console.error('Failed to update user data:', error);
      showError('Failed to update user details. Please try again.');
    } finally {
      setIsUpdatingUser(false);
    }
  };

  // Handle cancel editing
  const handleCancelEdit = () => {
    setIsEditingUser(false);
    // Reset to original data
    if (scannedUser) {
      setCurrentUserData(scannedUser);
    }
  };

  useEffect(() => {
    pdfFileRef.current = null;
    if (!isOpen || !shareSupported || isEditingUser || !finalBadgeConfig) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!badgeRendererRef.current) return;
      badgeRendererRef.current.buildPdfFile()
        .then((file) => { if (!cancelled) pdfFileRef.current = file; })
        .catch((err) => console.error('Badge PDF preparation failed:', err));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, shareSupported, isEditingUser, finalBadgeConfig, currentUserData, registrationData]);

  // Share the badge PDF to the device share sheet (print apps, AirPrint "Print", messaging).
  const handleSharePrint = async () => {
    if (!badgeRendererRef.current) {
      showError('Badge renderer not available');
      return;
    }
    setIsLoading(true);
    try {
      const file = pdfFileRef.current || await badgeRendererRef.current.buildPdfFile();
      pdfFileRef.current = file;
      await navigator.share({ files: [file], title: 'Badge' });
      onPrintStarted?.(null);
      onClose();
    } catch (error) {
      if (error?.name === 'NotAllowedError') {
        // Building took longer than the tap's activation window; the PDF is ready now.
        showError('Badge is ready. Tap Print again.');
      } else if (error?.name !== 'AbortError') {
        console.error('Share print error:', error);
        showError('Could not open the share sheet. Use Print Badge instead.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle print functionality
  const handlePrint = async () => {
    if (!badgeRendererRef.current) {
      showError('Badge renderer not available');
      return;
    }

    setIsLoading(true);
    try {
      const printWindow = await badgeRendererRef.current.printBadge();
      showSuccess('Badge sent to printer successfully!');
      onPrintStarted?.(printWindow);
      onClose();
    } catch (error) {
      console.error('Print error:', error);
      showError('Failed to print badge. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Loading state
  if (configLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p>Loading badge configuration...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (configError || !finalBadgeConfig) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <AlertTriangle className="w-5 h-5 mr-2" />
              Configuration Error
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-600">
              {configError ? 
                'Failed to load badge configuration.' : 
                'No badge configuration found for this event. Please set up the badge design first.'
              }
            </p>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              {!finalBadgeConfig && (
                <Button 
                  onClick={() => {
                    onClose();
                    // Navigate to badge config page
                    window.open(`/admin/events/${eventRef(eventId)}/badge-config`, '_blank');
                  }}
                >
                  Configure Badge
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50"
      onClick={handleBackdropClick}
    >
      <Card className={`w-full ${badgeOnly ? 'max-w-lg' : 'max-w-4xl'} max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden`}>
        <CardHeader className="flex-shrink-0 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <Printer className="w-5 h-5 mr-2" />
              {title}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Edit Mode - Full Screen User Details Editor */}
          {isEditingUser && currentUserData && formSchema.length > 0 ? (
            <div className="space-y-4">
              <UserDetailsEditor
                startInEditMode
                userData={currentUserData}
                formSchema={formSchema}
                onSave={handleSaveUserData}
                onCancel={handleCancelEdit}
                onChange={handleUserDataChange}
                isLoading={isUpdatingUser}
                currentScanPurpose={scannedUser?.purposeConfig?.purpose || scanResult?.purposeConfig?.purpose || scanResult?.purpose}
              />
            </div>
          ) : (
            /* Badge Preview Mode */
            <div className={badgeOnly ? 'flex justify-center' : 'grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6'}>
              {/* Badge Preview */}
              <div className="space-y-4">
                {!badgeOnly && <h3 className="text-lg font-medium">Badge Preview</h3>}
                <div className="flex justify-center overflow-x-auto">
                  <div className="min-w-0 flex-shrink-0">
                    <DynamicBadgeRenderer
                      ref={badgeRendererRef}
                      badgeConfig={finalBadgeConfig}
                      userData={finalUserData}
                      showPreview={true}
                      className="border shadow-lg max-w-full"
                    />
                  </div>
                </div>
              </div>

              {/* User Data Summary */}
              {!badgeOnly && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Registration Details</h3>
                  {currentUserData?.purposeConfig?.canUpdateUser !== false && formSchema.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsEditingUser(true)}
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </div>
                
                {finalUserData ? (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2 max-h-64 sm:max-h-96 overflow-y-auto">
                    {Object.entries(finalUserData).map(([key, value]) => {
                      if (!value || key === 'qrCodeBase64') return null;
                      
                      return (
                        <div key={key} className="flex flex-col sm:flex-row sm:justify-between py-1 border-b border-gray-200 gap-1">
                          <span className="font-medium text-gray-700 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </span>
                          <span className="text-gray-900 sm:text-right sm:max-w-xs break-words">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800">No user data available for badge printing.</p>
                  </div>
                )}

                {/* Badge Configuration Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Badge Configuration</h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p>Paper Size: {finalBadgeConfig.paperSize?.width}" × {finalBadgeConfig.paperSize?.height}"</p>
                    <p>Fields: {finalBadgeConfig.fields?.length || 0} configured</p>
                    <p>Visible Fields: {finalBadgeConfig.fields?.filter(f => f.visible).length || 0}</p>
                  </div>
                </div>
              </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          {!isEditingUser && (
            <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:space-x-2 pt-4 border-t mt-6 sticky bottom-0 bg-white">
              <Button
                variant="outline"
                onClick={onClose}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>

              {!badgeOnly && rfIdEnabled && currentUserData?.userId && (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (onAssignRfid) {
                      onAssignRfid({ userId: currentUserData.userId });
                    }
                  }}
                  className="w-full sm:w-auto"
                >
                  <RadioTower className="w-4 h-4 mr-2" />
                  Assign RFID
                </Button>
              )}

              {!badgeOnly && lockerEnabled && currentUserData?.userId && (
                <Button
                  variant="outline"
                  onClick={() => setShowLockerModal(true)}
                  className="w-full sm:w-auto text-amber-700 border-amber-300 hover:bg-amber-50"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Manage Locker
                </Button>
              )}

              {shareSupported && (
                <Button
                  variant="outline"
                  onClick={handleSharePrint}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share badge
                </Button>
              )}

              <Button
                onClick={handlePrint}
                disabled={isLoading}
                className="w-full sm:w-auto"
              >
                <Printer className="w-4 h-4 mr-2" />
                {isLoading ? 'Preparing...' : 'Print Badge'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Locker Management Modal for the scanned/selected user */}
      <LockerAssignModal
        open={showLockerModal}
        onClose={() => setShowLockerModal(false)}
        eventId={eventId}
        userId={currentUserData?.userId}
      />
    </div>
  );
};

export default DynamicBadgePrintModal;
